import URL from 'url';

import { ALLOWED_SIZES, DEFAULT_IMAGE_SIZE } from './imageSizes';
import { getImageUrl } from './getImageUrl';

export interface IImageSrcs {
  src: string;
  srcSet?: string;
}

interface IGetImageSrcs {
  alpha?: boolean;
  background?: string; // RGB value to use for transparent PNGs, ex: background=255,255,255
  crop?: boolean;
  getImageHost?: (host?: string) => string;
  height?: number;
  missingAssetPath?: string;
  noSrcModification?: boolean;
  quality?: number; // JPEG and WEBP image quality between 1-100 and divisible by 5. Defaults to 85
  resizeable?: boolean;
  size?: number; // takes the longest edge of the image and uses that as the resizing value
  src: string;
  type?: string; // overrides content negotiation based on Accepts header, options: jpeg, png, webp
  width?: number;
}

/**
 * Creates the source props used by <Image />
 *
 * @returns {{ src: string, srcSet: string }} src props spread on <StyledImage />
 */
export const getImageSrcs = ({
  alpha,
  background,
  crop,
  getImageHost,
  height,
  missingAssetPath,
  noSrcModification,
  quality,
  resizeable,
  size,
  src,
  type,
  width,
}: IGetImageSrcs): IImageSrcs => {
  if (noSrcModification) {
    return { src };
  }

  const url = URL.parse(src || 'missing.png');
  const originalPathname = url.pathname;
  url.protocol = 'https'; // request all images with https

  if (typeof getImageHost === 'function') {
    url.host = getImageHost(url.host); // get correct CDN host if applicable
  }

  // non-resizeable images are routed to the correct CDN
  // without changing the crop or size
  if (!resizeable && originalPathname) {
    const pathnameSegments = originalPathname.split('/');
    let sizeWidth = DEFAULT_IMAGE_SIZE;

    // get the size from the pathname
    if (ALLOWED_SIZES.includes(Number.parseInt(pathnameSegments[1], 10))) {
      sizeWidth = Number.parseInt(pathnameSegments[1], 10);
    } else if (ALLOWED_SIZES.includes(Number.parseInt(pathnameSegments[2], 10))) {
      // size is the 2nd index when it has crop in pathname, ex: /crop/3500/image.png
      sizeWidth = Number.parseInt(pathnameSegments[2], 10);
    }

    const imageUrl = getImageUrl({
      pathname: originalPathname,
      forceCrop: false,
      sizeWidth,
    });

    url.pathname = imageUrl.path;
    url.search = imageUrl.query;

    return {
      src: URL.format(url),
    };
  }

  // src is a backup for browsers without srcset
  // its path is updated with the crop prop and default size as query params
  if (originalPathname) {
    const imageUrl = getImageUrl({
      alpha,
      background,
      forceCrop: crop,
      height,
      pathname: originalPathname,
      quality,
      size,
      type,
      width,
    });

    url.pathname = imageUrl.path;
    url.search = imageUrl.query;
  }

  // srcSet lets the browser pick the best size from ALLOWED_SIZES
  const imageSrcs: IImageSrcs = {
    src: URL.format(url),
    srcSet: ALLOWED_SIZES.map((sizeNum) => {
      if (originalPathname) {
        const imageUrl = getImageUrl({
          alpha,
          background,
          forceCrop: crop,
          height,
          pathname: originalPathname,
          quality,
          size,
          sizeWidth: sizeNum,
          type,
          width,
        });

        url.pathname = imageUrl.path;
        url.search = imageUrl.query;
      }
      return `${URL.format(url)} ${sizeNum}w`;
    }).join(', '),
  };

  // Custom image src and srcset object if no actual asset to display.
  // It sets src as a 1x1 transparent image.
  const missingSrcs = {
    src: 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==',
    srcSet: '',
  };
  const isMissing = /missing.png/.test(src || '');

  return isMissing && missingAssetPath ? missingSrcs : imageSrcs;
};
